import { type FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { usePaymentMutation } from '@/api/queries/payment';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { opacityAnimation } from '@/common/config/motion-animations';
import { useClaimFreeDailyDice } from '@/common/hooks/claim-free-dice';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { openTgInvoice } from '@/common/lib/openTgInvoice';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type BuyDiceForTonPaymentResponseType } from '@/common/types/payment';
import { FullRecharge } from '@/components/common/FullRecharge/FullRecharge';
import { LightColorEffect } from '@/components/common/LightColorEffect/LightColorEffect';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H3 } from '@/components/Typography/Typography';
import { GreyBlurButton, PurpleButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import {
  changePaymentStatus,
  clearPendingPurchase,
  setPendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { BuyDicesContext } from '../context';

import css from './styles.module.scss';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';

const ConfirmBuy: FC = () => {
  const { t } = useTranslation();
  const [buyDicePayment, { isLoading }] = usePaymentMutation();
  const { claim: claimDailyDice, isClaiming } = useClaimFreeDailyDice();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const [getMyInfo] = useGetMyInfoMutation();
  const { activeOfferIndex } = useContext(BuyDicesContext);
  const { secondModal, thirdModal, closeModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const diceOffers = useAppSelector(
    (state) => state.user.extendedInfo?.dicePackages,
  );

  const activeOfferData = diceOffers && diceOffers[activeOfferIndex];
  const isFreeOffer = activeOfferData?.free !== undefined;
  const isFreeDiceClaimed = activeOfferData?.free === false;
  const usdPrice = useMemo(
    () =>
      multiplication(
        activeOfferData?.amountUsd ?? 0,
        additionalDiscountForUsdt.coefficient,
      ),
    [activeOfferData],
  );

  usePurchaseAttempt({
    item: 'dice',
    item_id: activeOfferData?.diceCount.toString() ?? '',
    price: activeOfferData?.amountUsd ?? 0,
    currency: 'usdt',
  });

  const buildAnaliticsPayload = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): {
    item: 'dice';
    item_id: string;
    price: number;
    currency: 'ton' | 'xtr' | 'usdt';
  } | null => {
    if (!activeOfferData) return null;

    return {
      item: 'dice',
      item_id: activeOfferData.diceCount.toString(),
      price: activeOfferData.amountUsd,
      currency,
    };
  };

  const markPaymentPending = (currency: 'ton' | 'xtr'): void => {
    const payload = buildAnaliticsPayload(currency);

    if (!payload) return;

    dispatch(setPendingPurchase(payload));
    SendAnalitics.purchasePending(payload);
  };

  const sendUsdtSuccessAnalitics = (): void => {
    const payload = buildAnaliticsPayload('usdt');

    if (!payload) return;

    SendAnalitics.purchaseSuccessfull(payload);
  };

  const buyDice = async (currency: 'XTR' | 'TON'): Promise<void> => {
    try {
      if (!activeOfferData) return;
      const body = {
        amount: activeOfferData.diceCount,
        currency,
        type: 'dice',
      } as const;

      if (currency === 'TON') {
        connectWalletAndDoTransaction(
          async () =>
            (await buyDicePayment(
              body,
            ).unwrap()) as BuyDiceForTonPaymentResponseType,
          {
            onSuccess: () => {
              ToastNotification(t('Payment will be credited soon.'), {
                img: dicePng,
                webp: diceWebp,
              });
              markPaymentPending('ton');
            },
            onError: () => {
              ToastNotification(t('Something went wrong, please try again'), {
                img: dicePng,
                webp: diceWebp,
              });
            },
          },
        );

        return;
      } else {
        const response = await buyDicePayment(body).unwrap();

        if ('link' in response) {
          openTgInvoice(response.link, {
            errorMessage: { image: { img: dicePng, webp: diceWebp } },
            onSuccess: () => {
              markPaymentPending('xtr');
            },
            playdeckRequestPayload: {
              amount: activeOfferData.amountStars,
              description: 'buy dice',
              externalId: `buy-dice-${activeOfferData.amountStars}`,
            },
          });

          return;
        }
      }
    } catch (error) {
      console.error(error);
      showToast(t('Something went wrong, please try again'));
    }
  };

  const buyUsdt = async (): Promise<void> => {
    if (!activeOfferData) return;

    try {
      await buyDicePayment({
        type: 'dice',
        amount: activeOfferData.diceCount,
        currency: 'USDT',
      }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      await getMyInfo();
      dispatch(changePaymentStatus('success'));
      sendUsdtSuccessAnalitics();
      ToastNotification(t('Success. Dice credited and ready to use'), {
        img: dicePng,
        webp: diceWebp,
      });
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: dicePng,
        webp: diceWebp,
      });
    }
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    buyDice(currency)
      .then(() => {
        dispatch(changePaymentStatus('pending'));
      })
      .catch(() => {
        dispatch(changePaymentStatus(undefined));
        dispatch(clearPendingPurchase());
      });
  };

  const claimFreeDice = async (): Promise<void> => {
    if (isFreeDiceClaimed) return;

    await claimDailyDice();
  };

  return (
    activeOfferData && (
      <motion.div
        {...opacityAnimation}
        className="w-full flex flex-col items-center"
      >
        <div className="relative w-24 h-24">
          <Picture
            className="w-full relative z-[2]"
            img={dicePng}
            webp={diceWebp}
            alt="dice"
          />
          <LightColorEffect
            lightClassName={css.purpleGradient}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85.04%] -z-[1]"
          />
        </div>
        <div className="flex flex-col items-center w-full relative z-[2] pt-4">
          <span className="font-black text-[2.5rem] leading-none mb-3">
            +{activeOfferData.diceCount}
          </span>
          {activeOfferData.diceCount >= 10 && <FullRecharge />}
          <div className="w-full mb-[1.875rem] mt-10">
            {isFreeOffer ? (
              <PurpleButton
                className="w-full"
                onClick={() => void claimFreeDice()}
                isLoading={isClaiming}
                inactive={isFreeDiceClaimed}
                disabled={isFreeDiceClaimed || isClaiming}
              >
                <H3 weight="extrabold" className="huge-text">
                  {isFreeDiceClaimed
                    ? t('Already claimed today')
                    : t('Get free dice')}
                </H3>
              </PurpleButton>
            ) : (
              <TONandStarsBuyButtons
                onClick={onClick}
                isLoading={isLoading}
                price={activeOfferData.amountUsd}
                usdPrice={usdPrice}
                discount={additionalDiscountForUsdt.percentage}
                starPrice={activeOfferData.amountStars}
                mustBeUsdtButton={true}
                onUsdtBuy={buyUsdt}
                variant="switch"
                classNames="!px-0"
              />
            )}
            <GreyBlurButton
              className="mt-3"
              text={t('Close')}
              onClick={() => {
                closeModal();
              }}
            />
          </div>
        </div>
      </motion.div>
    )
  );
};

export { ConfirmBuy };
